import type { Progress, AIGeneratedTrophy } from '../types';
import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';

const STORAGE_KEY = 'camera-trofeelor-progress';
const STORAGE_VERSION = 1;

interface CameraTrofeelorDB extends DBSchema {
  'ai-trophies': {
    key: string;
    value: AIGeneratedTrophy;
    indexes: { 'by-date': string };
  };
  'prompt-cache': {
    key: string;
    value: {
      hash: string;
      response: {
        trophyName: string;
        trophyDescription: string;
        imageDataUrl: string;
      };
      createdAt: string;
    };
  };
}

let dbPromise: Promise<IDBPDatabase<CameraTrofeelorDB>> | null = null;

const getDB = () => {
  if (!dbPromise) {
    dbPromise = openDB<CameraTrofeelorDB>('camera-trofeelor-db', 1, {
      upgrade(db) {
        const trophyStore = db.createObjectStore('ai-trophies', { keyPath: 'id' });
        trophyStore.createIndex('by-date', 'createdAt');
        db.createObjectStore('prompt-cache', { keyPath: 'hash' });
      },
    });
  }
  return dbPromise;
};

const defaultProgress: Progress = {
  lessonsCompleted: [],
  trophiesUnlocked: {},
  childrenNames: ['Copil 1', 'Copil 2'],
  gameHighScore: 0,
  createdAt: new Date().toISOString(),
  lastUpdated: new Date().toISOString(),
};

export const getProgress = (): Progress => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      saveProgress(defaultProgress);
      return defaultProgress;
    }
    const parsed = JSON.parse(stored);
    return {
      ...defaultProgress,
      ...parsed,
      trophiesUnlocked: parsed.trophiesUnlocked || {},
    };
  } catch (error) {
    console.error('Error reading progress:', error);
    return defaultProgress;
  }
};

export const saveProgress = (progress: Progress): void => {
  try {
    const updated = {
      ...progress,
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Error saving progress:', error);
  }
};

export const completeLesson = (lessonId: number, trophyId: string): Progress => {
  const progress = getProgress();
  
  if (!progress.lessonsCompleted.includes(lessonId)) {
    progress.lessonsCompleted.push(lessonId);
  }
  
  if (!progress.trophiesUnlocked[trophyId]) {
    progress.trophiesUnlocked[trophyId] = {
      unlockedAt: new Date().toISOString(),
    };
  }
  
  saveProgress(progress);
  return progress;
};

export const unlockTrophy = (trophyId: string): Progress => {
  const progress = getProgress();
  
  if (!progress.trophiesUnlocked[trophyId]) {
    progress.trophiesUnlocked[trophyId] = {
      unlockedAt: new Date().toISOString(),
    };
    saveProgress(progress);
  }
  
  return progress;
};

export const updateGameHighScore = (score: number): Progress => {
  const progress = getProgress();
  
  if (score > progress.gameHighScore) {
    progress.gameHighScore = score;
    saveProgress(progress);
  }
  
  return progress;
};

export const updateChildrenNames = (names: string[]): Progress => {
  const progress = getProgress();
  progress.childrenNames = names;
  saveProgress(progress);
  return progress;
};

export const resetProgress = (): Progress => {
  const fresh = {
    ...defaultProgress,
    createdAt: new Date().toISOString(),
    lastUpdated: new Date().toISOString(),
  };
  saveProgress(fresh);
  return fresh;
};

interface ExportData {
  version: number;
  exportedAt: string;
  progress: Progress;
  aiTrophies: AIGeneratedTrophy[];
}

export const exportProgress = async (): Promise<void> => {
  const progress = getProgress();
  const aiTrophies = await getAITrophies();
  
  const exportData: ExportData = {
    version: STORAGE_VERSION,
    exportedAt: new Date().toISOString(),
    progress,
    aiTrophies,
  };
  
  const jsonString = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const date = new Date().toISOString().split('T')[0];
  const filename = `camera-trofeelor-${date}.json`;
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const validateImportData = (data: unknown): data is ExportData => {
  if (!data || typeof data !== 'object') return false;
  
  const obj = data as Record<string, unknown>;
  
  if (typeof obj.version !== 'number') return false;
  if (typeof obj.exportedAt !== 'string') return false;
  if (!obj.progress || typeof obj.progress !== 'object') return false;
  
  const progress = obj.progress as Record<string, unknown>;
  if (!Array.isArray(progress.lessonsCompleted)) return false;
  if (!progress.trophiesUnlocked || typeof progress.trophiesUnlocked !== 'object') return false;
  
  return true;
};

export const importProgress = async (file: File): Promise<{ success: boolean; error?: string }> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = async (event) => {
      try {
        const content = event.target?.result as string;
        const data = JSON.parse(content);
        
        if (!validateImportData(data)) {
          resolve({ success: false, error: 'Format de fișier invalid' });
          return;
        }
        
        saveProgress(data.progress);
        
        if (data.aiTrophies && Array.isArray(data.aiTrophies)) {
          for (const trophy of data.aiTrophies) {
            await saveAITrophy(trophy);
          }
        }
        
        resolve({ success: true });
      } catch {
        resolve({ success: false, error: 'Eroare la citirea fișierului' });
      }
    };
    
    reader.onerror = () => {
      resolve({ success: false, error: 'Eroare la citirea fișierului' });
    };
    
    reader.readAsText(file);
  });
};

// AI Trophy functions with IndexedDB
export const saveAITrophy = async (trophy: AIGeneratedTrophy): Promise<void> => {
  const db = await getDB();
  await db.put('ai-trophies', trophy);
};

export const getAITrophies = async (): Promise<AIGeneratedTrophy[]> => {
  const db = await getDB();
  return db.getAllFromIndex('ai-trophies', 'by-date');
};

export const deleteAITrophy = async (id: string): Promise<void> => {
  const db = await getDB();
  await db.delete('ai-trophies', id);
};

// Prompt cache functions
export const simpleHash = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16);
};

export const getCachedResponse = async (prompt: string): Promise<{
  trophyName: string;
  trophyDescription: string;
  imageDataUrl: string;
} | null> => {
  const hash = simpleHash(prompt);
  const db = await getDB();
  const cached = await db.get('prompt-cache', hash);
  
  if (cached) {
    const oneDay = 24 * 60 * 60 * 1000;
    const cacheAge = Date.now() - new Date(cached.createdAt).getTime();
    if (cacheAge < oneDay) {
      return cached.response;
    }
    await db.delete('prompt-cache', hash);
  }
  
  return null;
};

export const cacheResponse = async (
  prompt: string,
  response: { trophyName: string; trophyDescription: string; imageDataUrl: string }
): Promise<void> => {
  const hash = simpleHash(prompt);
  const db = await getDB();
  await db.put('prompt-cache', {
    hash,
    response,
    createdAt: new Date().toISOString(),
  });
};

// Rate limiting
const RATE_LIMIT_KEY = 'camera-trofeelor-rate-limit';
const MAX_REQUESTS = 5;
const TIME_WINDOW = 60 * 1000; // 1 minute

interface RateLimitData {
  requests: number[];
}

export const canMakeRequest = (): boolean => {
  const now = Date.now();
  const data = getRateLimitData();
  
  // Filter out old requests
  data.requests = data.requests.filter(time => now - time < TIME_WINDOW);
  saveRateLimitData(data);
  
  return data.requests.length < MAX_REQUESTS;
};

export const recordRequest = (): void => {
  const data = getRateLimitData();
  data.requests.push(Date.now());
  saveRateLimitData(data);
};

export const getTimeUntilNextRequest = (): number => {
  const now = Date.now();
  const data = getRateLimitData();
  
  if (data.requests.length < MAX_REQUESTS) return 0;
  
  const oldestRequest = Math.min(...data.requests);
  return Math.max(0, TIME_WINDOW - (now - oldestRequest));
};

const getRateLimitData = (): RateLimitData => {
  try {
    const stored = localStorage.getItem(RATE_LIMIT_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // ignore
  }
  return { requests: [] };
};

const saveRateLimitData = (data: RateLimitData): void => {
  localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(data));
};
